import React from "react";
import StatusBadge from "./StatusBadge";
import { QuantityDisplay } from "./ProcurementDisplay";

export const LtrValue = ({ children, className = "" }) => (
  <bdi dir="ltr" className={`font-mono tabular-nums ${className}`.trim()}>
    {children ?? "—"}
  </bdi>
);

export const ProjectReference = ({ number, name }) => (
  <span className="inline-flex min-w-0 flex-col">
    <span className="font-semibold text-neutral-900 dark:text-neutral-50 truncate">
      {name || "—"}
    </span>
    {number && (
      <LtrValue className="text-xs text-primary-700 dark:text-primary-300">
        {number}
      </LtrValue>
    )}
  </span>
);

export const ManufacturingOrderReference = ({ value }) => (
  <LtrValue className="text-primary-700 dark:text-primary-300">
    {value || "—"}
  </LtrValue>
);
export const BOMReference = ({ value }) => (
  <LtrValue className="text-primary-700 dark:text-primary-300">
    {value || "—"}
  </LtrValue>
);

const STATUS_TONES = {
  draft: "neutral",
  planning: "neutral",
  planned: "info",
  pending: "warning",
  released: "primary",
  active: "success",
  in_progress: "primary",
  in_production: "primary",
  material_check: "warning",
  paused: "warning",
  on_hold: "warning",
  blocked: "danger",
  cancelled: "danger",
  rejected: "danger",
  inactive: "danger",
  completed: "success",
  approved: "success",
  done: "success",
};

const PROJECT_LABELS = {
  planning: "تخطيط",
  active: "نشط",
  on_hold: "معلق",
  completed: "مكتمل",
  cancelled: "ملغي",
  blocked: "متوقف",
  pending: "قيد الانتظار",
};
const PRODUCTION_LABELS = {
  draft: "مسودة",
  planned: "مخطط",
  material_check: "فحص المواد",
  released: "محرر",
  in_progress: "قيد التنفيذ",
  in_production: "قيد الإنتاج",
  completed: "مكتمل",
  cancelled: "ملغي",
  paused: "متوقف مؤقتًا",
  blocked: "متوقف",
};

export const ProjectStatusBadge = ({ status, size = "sm" }) => (
  <StatusBadge tone={STATUS_TONES[status] || "neutral"} size={size}>
    {PROJECT_LABELS[status] || status || "غير محدد"}
  </StatusBadge>
);

export const ProductionStatusBadge = ({ status, label, size = "sm" }) => (
  <StatusBadge tone={STATUS_TONES[status] || "neutral"} size={size}>
    {label || PRODUCTION_LABELS[status] || status || "غير محدد"}
  </StatusBadge>
);

export const ProductionProgress = ({ value, label = "التقدم" }) => {
  const safe = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="min-w-[8rem]" aria-label={`${label}: ${safe}%`}>
      <div className="flex items-center justify-between gap-2 text-xs text-neutral-500 dark:text-neutral-400">
        <span>{label}</span>
        <LtrValue>{safe}%</LtrValue>
      </div>
      <div
        className="mt-1 h-2 rounded-full bg-primary-100 dark:bg-primary-950"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={safe}
      >
        <div
          className="h-full rounded-full bg-primary-600"
          style={{ width: `${safe}%` }}
        />
      </div>
    </div>
  );
};

export const BOMComponentReference = ({ name, sku, quantity, unit }) => (
  <span className="inline-flex min-w-0 flex-col">
    <span className="truncate font-medium text-neutral-800 dark:text-neutral-100">
      {name || "—"}
    </span>
    <span className="text-xs text-neutral-500 dark:text-neutral-400">
      {sku && <LtrValue>{sku}</LtrValue>}{" "}
      {quantity != null && <QuantityDisplay value={quantity} unit={unit} />}
    </span>
  </span>
);

export const OperationsTableShell = ({ caption, children, className = "" }) => (
  <div
    className={`overflow-x-auto rounded-2xl border border-primary-100 bg-white shadow-sm ring-1 ring-primary-50 dark:border-primary-900/60 dark:bg-neutral-900 dark:ring-primary-950/40 ${className}`.trim()}
  >
    <table className="w-full text-sm">
      {caption && <caption className="sr-only">{caption}</caption>}
      {children}
    </table>
  </div>
);
